import { useState, useRef } from "react"
import { useFirestore } from "reactfire"
import { collection, addDoc, doc, updateDoc } from "firebase/firestore"
import { useActividad } from "../hooks/useActividad"

const ESTADOS = [
  { value: "bueno",       label: "✅ Bueno",       color: "#dcfce7", texto: "#166534" },
  { value: "deteriorado", label: "⚠️ Deteriorado", color: "#fef9c3", texto: "#854d0e" },
  { value: "perdido",     label: "❌ Perdido",     color: "#fee2e2", texto: "#991b1b" },
]

const CATEGORIAS = [
  "Camping y outdoor",
  "Uniformes y vestuario",
  "Material de arte y manualidades",
  "Equipamiento deportivo",
  "Documentación",
  "Herramientas",
  "Cocina y utensilios",
  "Primeros auxilios",
  "Electrónica",
  "Otro",
]

export default function InventarioModal({ unidadId, item, onCerrar }) {
  const firestore = useFirestore()
  const { registrar } = useActividad()
  const editando  = !!item
  const fotoRef   = useRef(null)

  const [form, setForm] = useState({
    nombre:      item?.nombre      || "",
    descripcion: item?.descripcion || "",
    cantidad:    item?.cantidad    || 1,
    estado:      item?.estado      || "bueno",
    categoria:   item?.categoria   || "",
    ubicacion:   item?.ubicacion   || "",
    fotoUrl:     item?.fotoUrl     || "",
  })
  const [fotoPreview, setFotoPreview] = useState(item?.fotoUrl || null)
  const [guardando,   setGuardando]   = useState(false)
  const [error,       setError]       = useState("")

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm(f => ({ ...f, [name]: name === "cantidad" ? Math.max(1, parseInt(value) || 1) : value }))
    setError("")
  }

  const handleFoto = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const canvas = document.createElement("canvas")
    const ctx    = canvas.getContext("2d")
    const img    = new Image()
    const reader = new FileReader()
    reader.onload = (ev) => {
      img.onload = () => {
        const MAX = 300
        const ratio = Math.min(MAX / img.width, MAX / img.height)
        canvas.width  = img.width  * ratio
        canvas.height = img.height * ratio
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        const base64 = canvas.toDataURL("image/jpeg", 0.75)
        setFotoPreview(base64)
        setForm(f => ({ ...f, fotoUrl: base64 }))
      }
      img.src = ev.target.result
    }
    reader.readAsDataURL(file)
  }

  const guardar = async () => {
    if (!form.nombre.trim()) { setError("El nombre es obligatorio."); return }
    setGuardando(true)
    setError("")
    const data = {
      nombre:      form.nombre.trim(),
      descripcion: form.descripcion.trim() || null,
      cantidad:    parseInt(form.cantidad) || 1,
      estado:      form.estado,
      categoria:   form.categoria || null,
      ubicacion:   form.ubicacion.trim() || null,
      fotoUrl:     form.fotoUrl || null,
    }
    try {
      if (editando) {
        await updateDoc(doc(firestore, "unidades", unidadId, "inventario", item.id), data)
        await registrar({ accion: "editó", entidad: "inventario", nombre: data.nombre, unidadId })
      } else {
        await addDoc(collection(firestore, "unidades", unidadId, "inventario"), {
          ...data,
          creadoEn: { seconds: Math.floor(Date.now() / 1000) },
        })
        await registrar({ accion: "creó", entidad: "inventario", nombre: data.nombre, unidadId })
      }
      onCerrar()
    } catch (err) {
      setError("Error al guardar: " + err.message)
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onCerrar()}>
      <div className="modal-box" style={{ maxWidth: 560 }}>
        <button className="modal-cerrar" onClick={onCerrar}>×</button>
        <h2 className="modal-titulo">
          {editando ? "✏️ Editar ítem" : "📦 Nuevo ítem"}
        </h2>

        {/* Foto */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginBottom: 24 }}>
          <div
            onClick={() => fotoRef.current?.click()}
            style={{
              width: 100, height: 100, borderRadius: 14,
              background: "var(--verde-suave)", border: "2px dashed var(--verde-claro)",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", overflow: "hidden",
            }}
          >
            {fotoPreview
              ? <img src={fotoPreview} alt="foto" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span style={{ fontSize: 36, opacity: 0.4 }}>📷</span>
            }
          </div>
          <button type="button" className="btn-secondary" style={{ fontSize: 12, padding: "6px 16px" }}
            onClick={() => fotoRef.current?.click()}>
            {fotoPreview ? "Cambiar foto" : "Subir foto"}
          </button>
          <input ref={fotoRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFoto} />
        </div>

        {/* Nombre */}
        <div className="form-group">
          <label className="form-label">Nombre del ítem</label>
          <input className="form-input" name="nombre" value={form.nombre}
            onChange={handleChange} placeholder="Ej: Carpa 4 personas, Linterna, Botiquín..." />
        </div>

        {/* Descripción */}
        <div className="form-group">
          <label className="form-label">Descripción <span style={{ fontWeight: 400, color: "var(--gris)" }}>(opcional)</span></label>
          <textarea className="form-input" name="descripcion" value={form.descripcion}
            onChange={handleChange} rows={2} style={{ resize: "vertical", minHeight: 60 }}
            placeholder="Detalles adicionales sobre el ítem..." />
        </div>

        {/* Cantidad + Estado */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Cantidad</label>
            <input className="form-input" type="number" name="cantidad" min="1"
              value={form.cantidad} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label className="form-label">Estado</label>
            <select className="form-select" name="estado" value={form.estado} onChange={handleChange}>
              {ESTADOS.map(e => (
                <option key={e.value} value={e.value}>{e.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Categoría + Ubicación */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Categoría</label>
            <select className="form-select" name="categoria" value={form.categoria} onChange={handleChange}>
              <option value="">— Sin categoría —</option>
              {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Ubicación <span style={{ fontWeight: 400, color: "var(--gris)" }}>(opcional)</span></label>
            <input className="form-input" name="ubicacion" value={form.ubicacion}
              onChange={handleChange} placeholder="Ej: Bodega, Sala de reuniones..." />
          </div>
        </div>

        {error && <div style={{ color: "var(--rojo)", fontSize: 13, marginBottom: 12 }}>⚠ {error}</div>}

        <div className="form-actions">
          <button className="btn-secondary" onClick={onCerrar}>Cancelar</button>
          <button className="btn-primary" onClick={guardar} disabled={guardando}>
            {guardando ? "Guardando..." : editando ? "Guardar cambios" : "Agregar ítem"}
          </button>
        </div>
      </div>
    </div>
  )
}
